import React, { useEffect, useState } from "react";
import { T } from "../Translate";
import { isValidPhone, isValidEmail } from "../../utils/contactLinks";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Box,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  Skeleton,
  DialogContent,
  DialogActions,
  Select,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  collection,
  onSnapshot,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { auth, db, getToken, uploadImage, uploadVideo } from "../../firebase";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../../icons";
import { VideoLibrary as VideoLibraryIcon } from "../../icons";
import { Search as SearchIcon } from "../../icons";
import { Edit as EditIcon } from "../../icons";
import { Delete as DeleteIcon } from "../../icons";
import { Flight as FlightIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { isLoggedIn } from "../../session";
import Atras from "../BackButton";
import BottomNav from "../BottomToolbar";
import { pageHeaderInnerSx, pageContentSx } from "../../pageLayout";
import { brandColors, barColors } from "../../theme";
import { uploadButtonSx, uploadProgressSx, removeMediaButtonSx, removeMediaIconSx } from "../mediaFormStyles";
import SortableImageGrid from "../../components/SortableImageGrid";
import FeatureListingButton from "../../components/FeatureListingButton";

import SearchClearAdornment from "../../components/SearchClearAdornment";
const CATEGORY_STYLES = {
  alojamiento: { color: "#4a90e2", bgcolor: "rgba(74,144,226,0.16)", label: <T>Alojamiento</T> },
  compra: { color: "#50e3c2", bgcolor: "rgba(80,227,194,0.16)", label: <T>Compra</T> },
  alquiler: { color: "#ed8936", bgcolor: "rgba(237,137,54,0.16)", label: <T>Alquiler</T> },
  combo: { color: "#9b51e0", bgcolor: "rgba(155,81,224,0.16)", label: <T>Combo Vuelo + Alojamiento</T> },
};

const FORM_VALIDATION = {
  whatsapp: {
    validate: (value) =>
      value && !isValidPhone(value) ? <T>Ingresá un número válido con código de país (ej: +5491122334455)</T> : "",
  },
  paypalEmail: {
    validate: (value) => (value && !isValidEmail(value) ? <T>Ingresá un correo de PayPal válido</T> : ""),
  },
  title: {
    required: true,
    minLength: 5,
    maxLength: 100,
    validate: (value) => {
      if (!value) return <T>El título es obligatorio</T>;
      if (value.length < 5) return <T>El título debe tener al menos 5 caracteres</T>;
      if (value.length > 100) return <T>El título no puede exceder 100 caracteres</T>;
      return "";
    },
  },
  description: {
    required: true,
    minLength: 20,
    maxLength: 1000,
    validate: (value) => {
      if (!value) return <T>La descripción es obligatoria</T>;
      if (value.length < 20) return <T>La descripción debe tener al menos 20 caracteres</T>;
      if (value.length > 1000) return <T>La descripción no puede exceder 1000 caracteres</T>;
      return "";
    },
  },
  price: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El precio es obligatorio</T>;
      if (isNaN(value) || value <= 0) return <T>El precio debe ser un número válido mayor que 0</T>;
      return "";
    },
  },
  place: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El lugar es obligatorio</T>;
      return "";
    },
  },
  category: {
    required: true,
    validate: (value) => {
      if (!value) return <T>La categoría es obligatoria</T>;
      return "";
    },
  },
  city: {
    required: true,
    validate: (value) => {
      if (!value) return <T>La ciudad es obligatoria</T>;
      if (value.length < 2) return <T>El nombre de la ciudad es demasiado corto</T>;
      return "";
    },
  },
  images: {
    required: true,
    validate: (value) => {
      if (!Array.isArray(value) || value.length === 0) return <T>Debe mantener al menos una imagen</T>;
      if (value.length > 10) return <T>No puede tener más de 10 imágenes</T>;
      return "";
    },
  },
  youtubeUrl: {
    required: false,
    validate: (value) => {
      if (!value) return "";
      const isValid = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{11}/.test(value.trim());
      return isValid ? "" : <T>Ingresá un link de YouTube válido</T>;
    },
  },
};

const CONTINENTS = [
  "África",
  "Asia",
  "América del Norte",
  "América del Sur",
  "Europa",
  "Medio Oriente",
  "Oceanía",
  "Antártida",
];

const MyProperties = () => {
  const [properties, setProperties] = useState([]);
  // Arranca en true: el gate del Skeleton es `loading && filteredProperties.length === 0`,
  // y el fetch inicial recién pone loading en true dentro del efecto de mount.
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Validar un campo específico
  const validateField = (name, value) => {
    const fieldValidation = FORM_VALIDATION[name];
    if (!fieldValidation) return "";
    return fieldValidation.validate(value);
  };

  // Validar todos los campos
  const validateForm = (property) => {
    if (!property) return { errors: {}, isValid: false };
    
    const errors = {};
    let isValid = true;
  
    Object.keys(FORM_VALIDATION).forEach((fieldName) => {
      const error = validateField(fieldName, property[fieldName]);
      if (error) {
        errors[fieldName] = error;
        isValid = false;
      }
    });
  
    return { errors, isValid };
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDelete = async (propertyId) => {
    try {
      const propertyRef = doc(db, "places", propertyId);
      await deleteDoc(propertyRef);
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
    } finally {
      setDeleteTargetId(null);
    }
  };

  const handleEditClick = (property) => {
    setSelectedProperty(property);
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedProperty(null);
    setFormErrors({});
  };

  const handleSaveEdit = async () => {
    if (!selectedProperty) return;
  
    // Validar todos los campos antes de guardar
    const { errors, isValid } = validateForm(selectedProperty);
    
    if (!isValid) {
      setFormErrors(errors);
      return;
    }
  
    try {
      const propertyRef = doc(db, "places", selectedProperty.id);
      const docSnap = await getDoc(propertyRef);

      if (!docSnap.exists()) {
        throw new Error("No se encontró el documento del vehículo.");
      }

      await updateDoc(propertyRef, {
        title: selectedProperty.title,
        description: selectedProperty.description,
        price: selectedProperty.price,
        city: selectedProperty.city,
        place: selectedProperty.place,
        category: selectedProperty.category,
        images: selectedProperty.images,
        video: selectedProperty.video ?? null,
        youtubeUrl: selectedProperty.youtubeUrl ?? "",
        whatsapp: (selectedProperty.whatsapp || "").trim(),
        paypalEmail: selectedProperty.category === "alojamiento" ? (selectedProperty.paypalEmail || "").trim() : "",
      });
      
      handleCloseModal();
    } catch (error) {
      setFormErrors(prev => ({
        ...prev,
        submit: <T>Error al actualizar el vehículo:</T> + error.message
      }));
    }
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    if (!files.length) return;

    // Validar límite de imágenes
    if (selectedProperty.images.length + files.length > 10) {
      setFormErrors(prev => ({
        ...prev,
        images: <T>No puede subir más de 10 imágenes</T>
      }));
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      const updatedImages = [...selectedProperty.images, ...imageUrls];
      setSelectedProperty(prev => ({
        ...prev,
        images: updatedImages
      }));

      // Validar imágenes después de la actualización
      const error = validateField('images', updatedImages);
      setFormErrors(prev => ({
        ...prev,
        images: error
      }));

    } catch (error) {
      setFormErrors(prev => ({
        ...prev,
        images: <T>Error al subir las imágenes</T>
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = selectedProperty.images.filter((_, i) => i !== index);
    setSelectedProperty(prev => ({
      ...prev,
      images: updatedImages
    }));

    // Validar imágenes después de eliminar
    const error = validateField('images', updatedImages);
    setFormErrors(prev => ({
      ...prev,
      images: error
    }));
  };

const handleVideoUpload = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  setLoading(true);
  try {
    const videoUrl = await uploadVideo(file);
    setSelectedProperty(prev => ({ ...prev, video: videoUrl }));
  } catch (error) {
    setFormErrors(prev => ({
      ...prev,
      video: <T>Error al subir el video</T>
    }));
  } finally {
    setLoading(false);
  }
};

const handleRemoveVideo = () => {
  setSelectedProperty(prev => ({ ...prev, video: null }));
};

const handleInputChange = (e) => {
  const { name, value } = e.target;
  const updatedProperty = {
    ...selectedProperty,
    [name]: value
  };
  
  setSelectedProperty(updatedProperty);
  
  // Validar el campo específico que cambió
  const error = validateField(name, value);
  setFormErrors(prev => ({
    ...prev,
    [name]: error
  }));

  // También validar todo el formulario para actualizar el estado del botón
  validateForm(updatedProperty);
};


  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  useEffect(() => {
    const checkAuthAndFetchProperties = async () => {
      if (!isLoggedIn()) {
        // Redirect to login page
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }

        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "places"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const propertiesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProperties(propertiesData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener propiedades:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchProperties();
  }, []);

  

  const filteredProperties = properties.filter((property) => {
    const searchMatch =
      property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.price.toString().includes(searchTerm) ||
      property.city.toString().includes(searchTerm) ||
      property.place.toLowerCase().includes(searchTerm.toLowerCase());

    return searchMatch;
  });



  const styles = {
    container: {
      mt: 25,
      mb: 20,
    },
  };

  return (
    <Container>
      <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
        <Toolbar sx={{ padding: "10px 0" }}>
          <Box sx={pageHeaderInnerSx}>
            <Atras color="inherit" />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              <T>Mis Propiedades</T>
            </Typography>
          </Box>
        </Toolbar>
        <Box sx={{ width: "100%", borderTop: `1px solid ${barColors.border}` }}>
          <Toolbar disableGutters sx={{ py: 1.5, px: 0 }}>
            <Box sx={pageHeaderInnerSx}>
              <TextField
                placeholder="Buscar propiedad..."
                value={searchTerm}
                onChange={handleSearchChange}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: <SearchClearAdornment value={searchTerm} onChange={handleSearchChange} />,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: barColors.textMuted }} />
                    </InputAdornment>
                  ),
                  sx: {
                    color: barColors.text,
                    backgroundColor: barColors.activeBg,
                    borderRadius: "999px",
                    "& fieldset": { border: "none" },
                    "&:hover fieldset": { border: "none" },
                    "&.Mui-focused fieldset": { border: `1px solid ${barColors.border}` },
                  },
                }}
              />
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      <Container maxWidth={false} disableGutters sx={{ ...pageContentSx, ...styles.container }}>
        {loading ? (
          <Grid container spacing={2} sx={{ mb: 20 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item key={i} xs={12} sm={6} md={4} lg={4}>
                <Box sx={{ p: 2, backgroundColor: brandColors.backgroundPaper, border: `1px solid ${brandColors.divider}`, borderRadius: "12px" }}>
                  <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: "8px", mb: 2 }} />
                  <Skeleton variant="text" width="80%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Skeleton variant="rectangular" width="48%" height={36} sx={{ borderRadius: "6px" }} />
                    <Skeleton variant="rectangular" width="48%" height={36} sx={{ borderRadius: "6px" }} />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : filteredProperties.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              minHeight: "45vh",
              gap: 1.5,
              color: "text.secondary",
            }}
          >
            <FlightIcon sx={{ fontSize: 56, color: "text.disabled" }} />
            <Typography sx={{ color: "text.secondary" }}>
              <T>No tenés propiedades publicadas todavía.</T>
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mb: 20 }}>
            {filteredProperties.map((property) => {
              const images = Array.isArray(property.images)
                ? property.images
                : property.images
                ? [property.images]
                : [];
              const categoryStyle = CATEGORY_STYLES[property.category];

              return (
                <Grid item key={property.id} xs={12} sm={6} md={4} lg={4}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      backgroundColor: brandColors.backgroundPaper,
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Etiqueta en su propia fila: al lado del título lo dejaba en una columna angosta. */}
                      {categoryStyle && (
                        <Chip
                          size="small"
                          label={categoryStyle.label}
                          sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: categoryStyle.color,
                            bgcolor: categoryStyle.bgcolor,
                          }}
                        />
                      )}
                      <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 1, lineHeight: 1.4, height: "2.8em", overflowWrap: "anywhere", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {property.title}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "text.secondary",
                          mb: 1.5,
                          lineHeight: 1.5,
                          height: "4.5em",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {property.description}
                      </Typography>

                      <Typography sx={{ fontSize: 20, fontWeight: 700, color: "success.main", mb: 1.5 }}>
                        $ {Number(property.price).toLocaleString()}
                      </Typography>

                      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                        {[
                          { label: <T>Ciudad</T>, value: property.city },
                          { label: <T>Lugar</T>, value: property.place },
                        ].map((item, i) => (
                          <Grid item xs={6} key={i}>
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                              {item.label}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.5, minHeight: "1.5em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {item.value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1.5 }}>
                        <T>Publicado el</T> {formatDate(property.createdAt)}
                      </Typography>

                      {images.length > 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            overflowX: "auto",
                            pb: 0.5,
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                        >
                          {images.map((image, index) => (
                            <CardMedia
                              key={index}
                              component="img"
                              image={image}
                              alt={`Imagen de ${property.title}`}
                              sx={{ width: 90, height: 90, borderRadius: 2, objectFit: "cover", flexShrink: 0 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            height: 90,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 2,
                            backgroundColor: "action.hover",
                            color: "text.disabled",
                          }}
                        >
                          <Typography sx={{ fontSize: 13 }}><T>No hay imágenes</T></Typography>
                        </Box>
                      )}
                    </CardContent>


                    {(property.bookedRanges || []).length > 0 && (
                      <Typography sx={{ px: 2, pb: 1, fontSize: 12, color: "text.secondary" }}>
                        <T>Reservas:</T>{" "}
                        {property.bookedRanges
                          .map((r) => `${new Date(`${r.checkIn}T12:00:00`).toLocaleDateString()} – ${new Date(`${r.checkOut}T12:00:00`).toLocaleDateString()}`)
                          .join(" · ")}
                      </Typography>
                    )}
                    {!property.sourceUrl && <FeatureListingButton collection="places" listing={property} />}
                    <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(property)}
                        sx={{ textTransform: "none", bgcolor: brandColors.action, "&:hover": { bgcolor: brandColors.actionHover } }}
                      >
                        <T>Editar</T>
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteTargetId(property.id)}
                        sx={{ textTransform: "none" }}
                      >
                        <T>Eliminar</T>
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      <Dialog open={!!deleteTargetId} onClose={() => setDeleteTargetId(null)}>
        <DialogTitle><T>Eliminar propiedad</T></DialogTitle>
        <DialogContent>
          <Typography>
            <T>Esta acción no se puede deshacer. ¿Querés eliminar esta propiedad?</T>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)}><T>Cancelar</T></Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteTargetId)}>
            <T>Eliminar</T>
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle><T>Editar Propiedad</T></DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label={<T>Título</T>}
            type="text"
            fullWidth
            variant="outlined"
            value={selectedProperty?.title || ""}
            onChange={handleInputChange}
            error={!!formErrors.title}
            helperText={formErrors.title}
            required
          />
          <TextField
            margin="dense"
            name="description"
            label={<T>Descripción</T>}
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={selectedProperty?.description || ""}
            onChange={handleInputChange}
            error={!!formErrors.description}
            helperText={formErrors.description}
            required
          />
          <TextField
            margin="dense"
            name="price"
            label={<T>Precio en USD</T>}
            type="number"
            fullWidth
            variant="outlined"
            value={selectedProperty?.price || ""}
            onChange={handleInputChange}
            error={!!formErrors.price}
            helperText={formErrors.price}
            required
          />
          <TextField
            margin="dense"
            name="whatsapp"
            label={<T>WhatsApp de contacto (opcional)</T>}
            type="tel"
            fullWidth
            variant="outlined"
            value={selectedProperty?.whatsapp || ""}
            onChange={handleInputChange}
            error={!!formErrors.whatsapp}
            helperText={formErrors.whatsapp || <T>Con código de país, ej: +5491122334455. Se muestra en la publicación con un botón de WhatsApp.</T>}
          />
          {selectedProperty?.category === "alojamiento" && (
            <TextField
              margin="dense"
              name="paypalEmail"
              label={<T>Correo de PayPal para recibir reservas (opcional)</T>}
              type="email"
              fullWidth
              variant="outlined"
              value={selectedProperty?.paypalEmail || ""}
              onChange={handleInputChange}
              error={!!formErrors.paypalEmail}
              helperText={formErrors.paypalEmail || <T>Si lo completás, los huéspedes pueden reservar con una seña del 30% que va directo a tu PayPal.</T>}
            />
          )}
          <TextField
            margin="dense"
            name="city"
            label={<T>Ciudad</T>}
            type="text"
            fullWidth
            variant="outlined"
            value={selectedProperty?.city || ""}
            onChange={handleInputChange}
            error={!!formErrors.city}
            helperText={formErrors.city}
            required
          />
          <Typography sx={{ mt: 2 }}><T>Tipo de publicación:</T></Typography>
          <TextField
            select
            margin="dense"
            name="category"
            label={<T>Categoría</T>}
            fullWidth
            variant="outlined"
            value={selectedProperty?.category || ""}
            onChange={handleInputChange}
            error={!!formErrors.category}
            helperText={formErrors.category}
            required
            SelectProps={{ MenuProps: { sx: { zIndex: 20001 } } }}
          >
            <MenuItem value="alojamiento"><T>Alojamiento</T></MenuItem>
            <MenuItem value="compra"><T>Compra</T></MenuItem>
            <MenuItem value="alquiler"><T>Alquiler</T></MenuItem>
            <MenuItem value="combo"><T>Combo Vuelo + Alojamiento</T></MenuItem>
          </TextField>
          
          <Typography sx={{ mt: 2 }}><T>Seleccione un lugar:</T></Typography>
          <Select
            name="place"
            value={selectedProperty?.place || ""}
            onChange={handleInputChange}
            displayEmpty
            fullWidth
            error={!!formErrors.place}
            required
            MenuProps={{ sx: { zIndex: 20001 } }}
          >
            <MenuItem value="" disabled>
              <T>Selecciona un continente</T>
            </MenuItem>
            {CONTINENTS.map((continent) => (
              <MenuItem key={continent} value={continent}>
                {continent}
              </MenuItem>
            ))}
          </Select>
          {formErrors.place && (
            <Typography color="error" variant="caption">
              {formErrors.place}
            </Typography>
          )}

          {/* Sección de imágenes */}
          {(formErrors?.images || 
            (selectedProperty?.images?.length === 0)) && (
            <Alert 
              severity={formErrors?.images ? "error" : "warning"}
              sx={{ mt: 2, mb: 1 }}
            >
              {formErrors?.images || <T>Debe mantener al menos una imagen de la propiedad</T>}
            </Alert>
          )}

          <label htmlFor="upload-button" style={{ width: "100%" }}>
            <Button
              variant="contained"
              component="div"
              sx={{ ...uploadButtonSx(!!formErrors?.images), mt: 2 }}
              disabled={loading || (selectedProperty?.images?.length >= 10)}
            >
              <AddPhotoAlternateIcon />
              {selectedProperty?.images?.length >= 10
                ? <T>Límite de imágenes alcanzado</T>
                : <><T>Subir Imagen</T>{selectedProperty?.images?.length > 0
                    ? ` (${selectedProperty.images.length}/10)`
                    : 'es'}</>}
              {loading && (
                <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
              )}
            </Button>
          </label>

          <input
            type="file"
            accept="image/*"
            onClick={(event) => (event.target.value = null)}
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="upload-button"
            multiple
          />

          <SortableImageGrid
            images={selectedProperty?.images}
            onChange={(images) => setSelectedProperty((prev) => ({ ...prev, images }))}
            onRemove={handleRemoveImage}
            hasError={!!formErrors?.images}
          />

          <Typography sx={{ mt: 3 }}><T>Video (opcional):</T></Typography>
          {selectedProperty?.video ? (
            <Box sx={{ position: "relative", mt: 1, maxWidth: 320 }}>
              <video src={selectedProperty.video} controls style={{ width: "100%", borderRadius: "10px" }} />
              <IconButton sx={removeMediaButtonSx} onClick={handleRemoveVideo}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
            </Box>
          ) : (
            <>
              <label htmlFor="edit-upload-video-button" style={{ width: "100%" }}>
                <Button
                  variant="contained"
                  fullWidth
                  component="div"
                  sx={{ ...uploadButtonSx(false), mt: 1 }}
                  disabled={loading}
                >
                  <VideoLibraryIcon />
                  <T>Subir video de la propiedad</T>
                  {loading && (
                    <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                  )}
                </Button>
              </label>
              <input
                type="file"
                accept="video/*"
                onClick={(event) => (event.target.value = null)}
                onChange={handleVideoUpload}
                style={{ display: "none" }}
                id="edit-upload-video-button"
              />
            </>
          )}

          <TextField
            margin="dense"
            name="youtubeUrl"
            label={<T>Link de YouTube (opcional)</T>}
            type="text"
            fullWidth
            variant="outlined"
            placeholder="https://www.youtube.com/watch?v=..."
            value={selectedProperty?.youtubeUrl || ""}
            onChange={handleInputChange}
            error={!!formErrors.youtubeUrl}
            helperText={formErrors.youtubeUrl}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}><T>Cancelar</T></Button>
          <Button 
            onClick={handleSaveEdit}
            disabled={!selectedProperty || !validateForm(selectedProperty).isValid}
          >
            <T>Guardar</T>
          </Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </Container>
  );
};

export default MyProperties;
